import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { compressImage, validateImageFile, MAX_IMAGE_SIZE, normalizeFile } from '@/lib/utils/image';

interface UseProductImagesOptions {
  maxImages?: number;
  onError?: (error: string) => void;
  /** If true, images will be queued for cropping before being added */
  enableCropping?: boolean;
}

interface PendingCropImage {
  file: File;
  preview: string;
}

interface UseProductImagesReturn {
  images: File[];
  imagePreviews: string[];
  isCompressing: boolean;
  handleImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleAddImages: (fileList: FileList) => Promise<void>;
  handleRemoveImage: (index: number) => void;
  handleReorderImages: (fromIndex: number, toIndex: number) => void;
  resetImages: () => void;
  setImages: React.Dispatch<React.SetStateAction<File[]>>;
  setImagePreviews: React.Dispatch<React.SetStateAction<string[]>>;
  // Cropping support
  pendingCropImage: PendingCropImage | null;
  cropQueue: PendingCropImage[];
  handleCropComplete: (file: File, preview: string) => void;
  handleCropCancel: () => void;
}

export function useProductImages(options: UseProductImagesOptions = {}): UseProductImagesReturn {
  const { maxImages = 5, onError, enableCropping = true } = options;

  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isCompressing, setIsCompressing] = useState(false);

  // Cropping queue state
  const [cropQueue, setCropQueue] = useState<PendingCropImage[]>([]);
  const [pendingCropImage, setPendingCropImage] = useState<PendingCropImage | null>(null);

  // Use ref to always get current images length
  const imagesRef = useRef<File[]>([]);
  imagesRef.current = images;

  const handleError = useCallback((message: string) => {
    if (onError) {
      onError(message);
    } else {
      toast.error(message);
    }
  }, [onError]);

  // Process next image in crop queue
  const processNextInQueue = useCallback(() => {
    setCropQueue((queue) => {
      if (queue.length === 0) {
        setPendingCropImage(null);
        return queue;
      }
      const [next, ...rest] = queue;
      setPendingCropImage(next);
      return rest;
    });
  }, []);

  // Add image directly (after cropping or if cropping disabled)
  const addProcessedImage = useCallback(async (file: File, preview: string) => {
    const currentLength = imagesRef.current.length;
    if (currentLength >= maxImages) {
      handleError(`Maximum ${maxImages} images allowed`);
      return;
    }

    setImages((prev) => [...prev, file]);
    setImagePreviews((prev) => [...prev, preview]);
  }, [maxImages, handleError]);

  // Handle crop completion
  const handleCropComplete = useCallback((file: File, preview: string) => {
    addProcessedImage(file, preview);
    processNextInQueue();
  }, [addProcessedImage, processNextInQueue]);

  // Handle crop cancellation
  const handleCropCancel = useCallback(() => {
    processNextInQueue();
  }, [processNextInQueue]);

  const processAndAddImages = useCallback(async (fileList: FileList | File[]) => {
    const currentLength = imagesRef.current.length;
    const files = Array.from(fileList);
    const newFiles = files.slice(0, maxImages - currentLength);

    if (newFiles.length === 0) return;

    // Validate files first
    for (const file of newFiles) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        handleError(validation.error || 'Invalid image file');
        return;
      }
    }

    setIsCompressing(true);

    try {
      const imagesToProcess: PendingCropImage[] = [];

      for (const file of newFiles) {
        // Normalize filename to avoid encoding issues with accents/special chars
        let processedFile = normalizeFile(file);

        // Compress if file is larger than 3MB
        if (processedFile.size > MAX_IMAGE_SIZE) {
          toast.info(`Compression de ${processedFile.name}...`);
          processedFile = await compressImage(processedFile);
        }

        // Generate preview
        const preview = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(processedFile);
        });

        if (enableCropping) {
          // Add to crop queue
          imagesToProcess.push({ file: processedFile, preview });
        } else {
          // Add directly without cropping
          setImages((prev) => [...prev, processedFile]);
          setImagePreviews((prev) => [...prev, preview]);
        }
      }

      // If cropping is enabled, start the crop queue
      if (enableCropping && imagesToProcess.length > 0) {
        const [first, ...rest] = imagesToProcess;
        setPendingCropImage(first);
        setCropQueue(rest);
      }
    } catch (error) {
      console.error('Error processing images:', error);
      handleError('Échec du traitement des images');
    } finally {
      setIsCompressing(false);
    }
  }, [maxImages, handleError, enableCropping]);

  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await processAndAddImages(files);
  }, [processAndAddImages]);

  const handleAddImages = useCallback(async (fileList: FileList) => {
    if (!fileList || fileList.length === 0) return;
    await processAndAddImages(fileList);
  }, [processAndAddImages]);

  const handleRemoveImage = useCallback((index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleReorderImages = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    setImages((prev) => {
      const newImages = [...prev];
      const [movedImage] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, movedImage);
      return newImages;
    });

    setImagePreviews((prev) => {
      const newPreviews = [...prev];
      const [movedPreview] = newPreviews.splice(fromIndex, 1);
      newPreviews.splice(toIndex, 0, movedPreview);
      return newPreviews;
    });
  }, []);

  const resetImages = useCallback(() => {
    setImages([]);
    setImagePreviews([]);
    setCropQueue([]);
    setPendingCropImage(null);
  }, []);

  return {
    images,
    imagePreviews,
    isCompressing,
    handleImageSelect,
    handleAddImages,
    handleRemoveImage,
    handleReorderImages,
    resetImages,
    setImages,
    setImagePreviews,
    // Cropping support
    pendingCropImage,
    cropQueue,
    handleCropComplete,
    handleCropCancel,
  };
}
